import { useEffect, useRef, useState } from "react";

function ShippingFormModal({ isOpen, onClose, metodoData, onSubmit }) {
  const isEdit = !!metodoData;

  const [form, setForm] = useState({ nombre: "" });

  // ✅ alert del modal
  const [alert, setAlert] = useState(null); // { type, message }
  const alertTimerRef = useRef(null);

  // ✅ loading
  const [saving, setSaving] = useState(false);

  const showAlert = (type, message) => {
    setAlert({ type, message: String(message || "") });

    if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!isOpen) return;

    setForm({ nombre: metodoData?.nombre || "" });
    setAlert(null);
    setSaving(false);

    return () => {
      if (alertTimerRef.current) window.clearTimeout(alertTimerRef.current);
    };
  }, [isOpen, metodoData]);

  // ✅ ESC para cerrar
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;

    setAlert(null);

    const cleanName = String(form.nombre || "").trim();
    if (!cleanName) {
      showAlert("error", "Method name is required.");
      return;
    }

    setSaving(true);

    try {
      await onSubmit({ nombre: cleanName }); // si falla, debe lanzar error
      onClose?.();
    } catch (error) {
      console.error("Shipping method save failed:", error);
      showAlert("error", error?.message || "Could not save shipping method.");
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={ui.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose?.();
      }}
    >
      <div style={ui.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div style={ui.header}>
          <h2 style={ui.title}>
            {isEdit ? "Edit Shipping Method" : "Create Shipping Method"}
          </h2>
          <button style={ui.closeBtn} onClick={onClose} disabled={saving}>
            ✕
          </button>
        </div>

        {/* ✅ ALERT dentro del modal */}
        {alert && (
          <div
            style={{
              ...ui.alert,
              ...(alert.type === "error" ? ui.alertError : ui.alertInfo),
            }}
          >
            <span>{alert.message}</span>
            <button
              style={ui.alertX}
              onClick={() => setAlert(null)}
              disabled={saving}
              type="button"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={submit} style={ui.form}>
          <div style={ui.inputGroup}>
            <label style={ui.label}>Method Name</label>
            <input
              type="text"
              name="nombre"
              value={form.nombre}
              onChange={updateField}
              style={ui.input}
              required
              disabled={saving}
              autoFocus
            />
          </div>

          <div style={ui.footer}>
            <button type="button" style={ui.cancelBtn} onClick={onClose} disabled={saving}>
              Cancel
            </button>

            <button
              type="submit"
              style={{ ...ui.saveBtn, ...(saving ? ui.saveBtnDisabled : null) }}
              disabled={saving}
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const ui = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 2000,
  },

  modal: {
    width: "420px",
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 6px 22px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },

  header: {
    padding: "16px 22px",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  title: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    margin: 0,
  },

  closeBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "22px",
    color: "#6B7280",
    opacity: 0.9,
  },

  // ✅ Alert styles
  alert: {
    margin: "14px 22px 0",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px solid",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    fontSize: "13px",
    fontWeight: 700,
  },
  alertError: {
    background: "#FEF2F2",
    borderColor: "#FECACA",
    color: "#7F1D1D",
  },
  alertInfo: {
    background: "#EFF6FF",
    borderColor: "#BFDBFE",
    color: "#1E3A8A",
  },
  alertX: {
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: "16px",
    opacity: 0.75,
  },

  form: {
    padding: "22px",
  },

  inputGroup: {
    marginBottom: "18px",
  },

  label: {
    fontSize: "13px",
    color: "#374151",
    fontWeight: 600,
    marginBottom: "6px",
    display: "block",
  },

  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "15px",
    borderRadius: "8px",
    border: "1px solid #CBD5E1",
    outline: "none",
    background: "#F8FAFC",
    boxSizing: "border-box",
  },

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    paddingTop: "14px",
    borderTop: "1px solid #E5E7EB",
  },

  cancelBtn: {
    padding: "10px 16px",
    background: "#E5E7EB",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
  },

  saveBtn: {
    padding: "10px 18px",
    background: "#3B82F6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 700,
  },

  saveBtnDisabled: {
    opacity: 0.75,
    cursor: "not-allowed",
  },
};

export default ShippingFormModal;
